/**
 * JackpotModal Component
 *
 * Main jackpot experience modal with wheels, tickets, and results.
 * Provides the complete spin experience including jackpot secondary wheel.
 */

import { memo, useState, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { SpinWheel } from './SpinWheel';
import { JackpotWheel } from './JackpotWheel';
import { SpinResultDisplay } from './SpinResultDisplay';
import { JackpotCounter } from './JackpotCounter';
import { FreeBonusTracker } from './FreeBonusTracker';
import { MultipleEntitlementsView } from './MultipleEntitlementsView';
import { TicketPurchaseCard } from './TicketPurchaseCard';
import { useJackpot, useCheckin } from '../../hooks/useJackpot';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import type { SpinResult, JackpotWheelResult, EntitlementProgress } from '../../types/jackpot';

interface JackpotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToPricing?: () => void;
}

type ModalPhase = 'idle' | 'spinning' | 'jackpot' | 'result';

export const JackpotModal = memo(function JackpotModal({
  isOpen,
  onClose,
  onNavigateToPricing,
}: JackpotModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tickets, spin, spinAll, purchaseTickets } = useJackpot();
  const { checkin, freeBonusProgress } = useCheckin();

  const [phase, setPhase] = useState<ModalPhase>('idle');
  const [currentResult, setCurrentResult] = useState<SpinResult | null>(null);
  const [activeTab, setActiveTab] = useState<'spin' | 'progress'>('spin');

  // Get entitlements from tickets response (backend includes them there)
  const entitlements: EntitlementProgress[] = tickets.entitlements;

  // Perform check-in when modal opens to get free bonus progress
  useEffect(() => {
    if (isOpen && user) {
      checkin();
    }
  }, [isOpen, user, checkin]);

  // Handle spin button click
  const handleSpinStart = useCallback(async () => {
    if (tickets.tickets === 0 || spin.isSpinning) return;

    setPhase('spinning');
    const result = await spin.spin();

    if (result) {
      setCurrentResult(result);
      // If jackpot hit, show jackpot wheel
      if (result.main_wheel.triggers_jackpot) {
        setPhase('jackpot');
      }
    } else {
      // Spin failed
      setPhase('idle');
    }
  }, [tickets.tickets, spin]);

  // Handle main wheel animation complete
  const handleMainWheelComplete = useCallback((result: SpinResult) => {
    if (result.jackpot_wheel) {
      // Jackpot was hit, phase is already 'jackpot'
      return;
    }
    // Regular win, show result
    setPhase('result');
  }, []);

  // Handle jackpot wheel complete
  const handleJackpotWheelComplete = useCallback((_result: JackpotWheelResult) => {
    setPhase('result');
  }, []);

  // Handle result dismissal
  const handleResultDismiss = useCallback(() => {
    setPhase('idle');
    setCurrentResult(null);
    // Refresh tickets
    tickets.refetch();
  }, [tickets]);

  // Handle spin all
  const handleSpinAll = useCallback(async () => {
    if (tickets.tickets <= 1 || spinAll.isSpinning) return;

    const result = await spinAll.spinAll();
    if (result) {
      // For spin all, we show a summary result
      // Create a pseudo-result for display using backend field names
      const summaryResult: SpinResult = {
        success: true,
        spin_id: 'bulk',
        ticket_id: 'bulk',
        main_wheel: {
          slot_index: 0,
          prize_type: `${result.total_spins} spins`,
          tokens_won: result.total_tokens_won,
          triggers_jackpot: result.jackpot_hits > 0,
        },
        jackpot_wheel:
          result.jackpot_hits > 0
            ? {
                slot_index: 0,
                prize_type: 'bulk',
                percentage: 0,
                pool_at_spin: 0,
                tokens_won: 0,
                animation_degrees: 0,
              }
            : null,
        total_tokens_won: result.total_tokens_won,
        new_token_balance: result.new_token_balance,
        remaining_tickets: 0,
      };
      setCurrentResult(summaryResult);
      setPhase('result');
    }
  }, [tickets.tickets, spinAll]);

  // Handle ticket purchase
  const handlePurchaseTickets = useCallback(
    async (quantity: number) => {
      const result = await purchaseTickets.purchase(quantity);
      if (result) {
        // Refresh tickets after purchase
        tickets.refetch();
      }
    },
    [purchaseTickets, tickets]
  );

  // Close on ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="jackpot-modal-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto
                     bg-gradient-to-b from-slate-900 to-slate-950
                     rounded-2xl border border-slate-700 shadow-2xl
                     animate-slide-up"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg
                     text-slate-400 hover:text-white hover:bg-slate-800
                     transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="flex flex-col items-center gap-6 p-6">
          {/* Header */}
          <div className="text-center">
            <h2
              id="jackpot-modal-title"
              className="text-2xl font-bold text-amber-400 flex items-center justify-center gap-2"
            >
              <span role="img" aria-label="jackpot">
                {'\u{1F3B0}'}
              </span>{' '}
              {t('jackpot.tokenJackpot', 'Token Jackpot')}
            </h2>
            <div className="mt-3">
              <JackpotCounter />
            </div>
          </div>

          {/* Tabs (when logged in) */}
          {user && (
            <div className="flex gap-2 bg-slate-800/50 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('spin')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                           ${activeTab === 'spin' ? 'bg-amber-500 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {t('jackpot.spin', 'Spin')}
              </button>
              <button
                onClick={() => setActiveTab('progress')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                           ${activeTab === 'progress' ? 'bg-amber-500 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                {t('jackpot.progress', 'Progress')}
              </button>
            </div>
          )}

          {/* Spin Tab */}
          {activeTab === 'spin' && (
            <>
              {/* Tickets available */}
              {user && (
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-slate-400">
                    {t('jackpot.yourTickets', 'Your tickets')}:
                  </span>
                  <span className="font-bold text-amber-300">{tickets.tickets}</span>
                  <button
                    onClick={() => setActiveTab('progress')}
                    className="px-3 py-1 rounded-md text-xs font-medium
                               bg-amber-500/20 text-amber-400 border border-amber-500/30
                               hover:bg-amber-500/30 transition-colors"
                  >
                    {t('jackpot.buyMoreTickets')}
                  </button>
                </div>
              )}

              {/* Main wheel (when not showing jackpot wheel or result) */}
              {phase !== 'jackpot' && phase !== 'result' && (
                <SpinWheel
                  onSpinComplete={handleMainWheelComplete}
                  onSpinStart={handleSpinStart}
                  spinResult={currentResult}
                  disabled={!user || tickets.tickets === 0 || phase === 'spinning'}
                  isSpinning={spin.isSpinning}
                  className="my-4"
                />
              )}

              {/* Jackpot wheel (when jackpot hit) */}
              {phase === 'jackpot' && currentResult?.jackpot_wheel && (
                <div className="flex flex-col items-center animate-slide-up">
                  <h3 className="text-xl font-bold text-amber-300 mb-4 flex items-center gap-2">
                    <span role="img" aria-label="celebration">
                      {'\u{1F389}'}
                    </span>{' '}
                    {t('jackpot.jackpotSpin', 'JACKPOT SPIN!')}{' '}
                    <span role="img" aria-label="celebration">
                      {'\u{1F389}'}
                    </span>
                  </h3>
                  <JackpotWheel
                    poolAmount={currentResult.jackpot_wheel.pool_at_spin}
                    result={currentResult.jackpot_wheel}
                    onComplete={handleJackpotWheelComplete}
                    className="my-4"
                  />
                </div>
              )}

              {/* Result display */}
              {phase === 'result' && currentResult && (
                <SpinResultDisplay
                  result={currentResult}
                  onDismiss={handleResultDismiss}
                  className="my-4"
                />
              )}

              {/* Spin all button */}
              {user && tickets.tickets > 1 && phase === 'idle' && (
                <button
                  onClick={handleSpinAll}
                  disabled={spinAll.isSpinning}
                  className="text-sm text-amber-400/70 hover:text-amber-400 transition-colors"
                >
                  {t('jackpot.spinAllTickets', {
                    count: tickets.tickets,
                  })}{' '}
                  {'\u2192'}
                </button>
              )}

              {/* No tickets CTA */}
              {user && tickets.tickets === 0 && phase === 'idle' && (
                <div className="text-center text-slate-400 py-4">
                  <p>{t('jackpot.noTicketsAvailable', 'No tickets available')}</p>
                  <button
                    onClick={() => {
                      onClose();
                      onNavigateToPricing?.();
                    }}
                    className="mt-2 text-amber-400 hover:underline"
                  >
                    {t('jackpot.buyTokensForTickets', 'Buy tokens to get tickets')} {'\u2192'}
                  </button>
                </div>
              )}

              {/* Not logged in CTA */}
              {!user && (
                <div className="text-center text-slate-400 py-4">
                  <p>{t('jackpot.loginToSpin', 'Login to spin the wheel!')}</p>
                </div>
              )}
            </>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && user && (
            <div className="w-full space-y-6">
              {/* Free bonus tracker */}
              {freeBonusProgress && (
                <FreeBonusTracker streak={freeBonusProgress.current_streak ?? 0} />
              )}

              {/* Ticket purchase card */}
              <TicketPurchaseCard
                onPurchase={handlePurchaseTickets}
                isLoading={purchaseTickets.isPurchasing}
                error={purchaseTickets.error}
              />

              {/* Purchase entitlements */}
              {entitlements.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-3">
                    {t('jackpot.purchaseEntitlements', 'Purchase Entitlements')}
                  </h4>
                  <MultipleEntitlementsView entitlements={entitlements} />
                </div>
              )}
            </div>
          )}

          {/* Error display */}
          {spin.error && (
            <div className="w-full p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {spin.error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
